'use client';

import { c as _c } from "react/compiler-runtime";
import './search-bar.scss';
import * as z from 'zod';
import type { ElementType } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { debounce } from 'throttle-debounce';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import type { Sizes } from '@sola/ark-core/atoms/text/text.tsx';
import { Prominence, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Form } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { trackClick, trackSearch } from '@sola/tracking/lib/analytics/events.ts';
export const SearchBarProps = z.object({
  className: z.string().optional(),
  seoLevel: z.number().int().optional(),
  title: z.string().optional(),
  titleSize: z.enum(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']).optional(),
  placeholder: z.string().optional(),
  buttonText: z.string().optional(),
  defaultQuery: z.string().optional(),
  onQuerySubmit: z.function({
    input: [z.string()],
    output: z.void()
  }).optional(),
  onQueryChange: z.function({
    input: [z.string()],
    output: z.void()
  }).optional(),
  enableQuickSearch: z.boolean().optional(),
  focusOnLoad: z.boolean().optional()
});
export type SearchBarProps = z.infer<typeof SearchBarProps>;
export const SearchBar: React.FC<SearchBarProps> = t0 => {
  const $ = _c(40);
  const {
    seoLevel: t1,
    title,
    titleSize: t2,
    placeholder,
    buttonText,
    defaultQuery,
    onQuerySubmit,
    onQueryChange,
    enableQuickSearch: t3,
    focusOnLoad: t4
  } = t0;
  const seoLevel = t1 === undefined ? 3 : t1;
  const titleSize = t2 === undefined ? "sm" : t2;
  const enableQuickSearch = t3 === undefined ? false : t3;
  const focusOnLoad = t4 === undefined ? true : t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ["skippy"];
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const {
    t
  } = useTranslation(t5);
  let t6;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {};
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const searchRef = useRef(t6 as HTMLInputElement);
  let t7;
  if ($[2] !== focusOnLoad) {
    t7 = () => {
      if (searchRef.current && searchRef.current.focus && focusOnLoad) {
        searchRef.current.focus({
          preventScroll: true
        });
      }
    };
    $[2] = focusOnLoad;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  useEffect(t7, t8);
  let t9;
  if ($[5] !== onQueryChange) {
    t9 = debounce(300, queryString => {
      onQueryChange?.(queryString);
    }, {
      atBegin: false
    });
    $[5] = onQueryChange;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const handleQueryChangeRef = useRef(t9);
  let t10;
  let t11;
  if ($[7] !== onQueryChange) {
    t10 = () => {
      handleQueryChangeRef.current = debounce(300, queryString_0 => {
        onQueryChange?.(queryString_0);
      }, {
        atBegin: false
      });
    };
    t11 = [onQueryChange];
    $[7] = onQueryChange;
    $[8] = t10;
    $[9] = t11;
  } else {
    t10 = $[8];
    t11 = $[9];
  }
  useEffect(t10, t11);
  let t12;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = queryString_1 => {
      handleQueryChangeRef.current(queryString_1);
    };
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  const handleQueryChange = t12;
  let t13;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = debounce(1500, _temp, {
      atBegin: false
    });
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  const trackQueryChangeRef = useRef(t13);
  let t14;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = queryString_3 => {
      trackQueryChangeRef.current(queryString_3);
    };
    $[12] = t14;
  } else {
    t14 = $[12];
  }
  const trackQueryChange = t14;
  let t15;
  if ($[13] !== onQuerySubmit) {
    t15 = data => {
      if (onQuerySubmit) {
        onQuerySubmit(data.searchQuery);
      }
      if (searchRef.current) {
        searchRef.current.blur();
      }
    };
    $[13] = onQuerySubmit;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  const handleSubmit = t15;
  const Comp = `h${seoLevel}` as ElementType;
  let t16;
  if ($[15] !== defaultQuery) {
    t16 = {
      defaultValues: {
        searchQuery: defaultQuery
      }
    };
    $[15] = defaultQuery;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  if ($[17] !== Comp || $[18] !== title || $[19] !== titleSize) {
    t17 = title && <Text size={titleSize as Sizes} prominence={Prominence.Potent} variant={Variants.Heading} asChild={true}><Comp className="heading-neutral-amplified">{title}</Comp></Text>;
    $[17] = Comp;
    $[18] = title;
    $[19] = titleSize;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] !== enableQuickSearch) {
    t18 = enableQuickSearch ? e => {
      trackQueryChange(e.target.value);
      handleQueryChange(e.target.value);
    } : undefined;
    $[21] = enableQuickSearch;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== placeholder || $[24] !== t18) {
    t19 = <InputField name="searchQuery" inputMode="search" aria-label="search" placeholder={placeholder} iconName="search-sm" iconClasses="searchIcon" onChange={t18} inputRef={searchRef} />;
    $[23] = placeholder;
    $[24] = t18;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== buttonText || $[27] !== enableQuickSearch || $[28] !== t) {
    t20 = !enableQuickSearch && <ButtonStyle size="md"><button type="submit" onClick={() => {
        trackClick(buttonText || t("articles:search"));
      }}>{buttonText || t("articles:search")}</button></ButtonStyle>;
    $[26] = buttonText;
    $[27] = enableQuickSearch;
    $[28] = t;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t19 || $[31] !== t20) {
    t21 = <div className="SearchBar__input flex flex-row gap-comfy-sm">{t19}{t20}</div>;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t17 || $[34] !== t21) {
    t22 = <div className="SearchBar__container grid gap-comfy-sm">{t17}{t21}</div>;
    $[33] = t17;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== handleSubmit || $[37] !== t16 || $[38] !== t22) {
    t23 = <div className="SearchBar"><Form role="search" onSubmit={handleSubmit} options={t16}>{t22}</Form></div>;
    $[36] = handleSubmit;
    $[37] = t16;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  return t23;
};
function _temp(queryString_2) {
  trackSearch(queryString_2);
}